import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import toast from 'react-hot-toast';
import { EnhancedJournalFilters } from '../../components/journals/EnhancedJournalFilters';
import { JournalEntryView } from '../../components/journals/JournalEntryView';
import { NewJournalEntryModal } from '../../components/journals/NewJournalEntryModal';
import { JournalEntryEditModal } from '../../components/journals/JournalEntryEditModal';
import { ReverseJournalEntryModal } from '../../components/journals/ReverseJournalEntryModal';
import { UnifiedJournalList } from '../../components/journals/UnifiedJournalList';
import { Modal } from '../../components/ui/Modal';
import { useJournalEntries } from '../../hooks/useJournalEntries';
import { useJournalReversal } from '../../hooks/useJournalReversal';
import { useAgentEntries } from '../../hooks/useAgentEntries';
import { useEnhancedJournalFilters } from '../../hooks/useEnhancedJournalFilters';
import { useOrganization } from '../../hooks/useOrganization';
import { useJournalExport } from '../../hooks/useJournalExport';
import { useFiscalYearStore } from '../../stores/fiscalYearStore';
import { usePermissions } from '../../hooks/usePermissions';
import { CloudinaryService } from '../../services/cloudinary/CloudinaryService';
import type { JournalEntry } from '../../types/accounting';

export function JournalsPage() {
  const [showNewEntryModal, setShowNewEntryModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReverseModal, setShowReverseModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<JournalEntry | null>(null);
  
  const { currentFiscalYear } = useFiscalYearStore();
  
  // Permissions basées sur l'API /auth/me
  const { can } = usePermissions();
  const canCreateEntry = can('create_entries');
  const canValidateEntry = can('validate_entries');
  const canDeleteEntry = can('delete_entries');
  
  const { 
    entries, 
    loading, 
    pagination,
    addEntry, 
    updateEntry, 
    deleteEntry,
    submitEntry,
    approveEntry,
    rejectEntry,
    postEntry,
    validateAndPostEntry,
    getAvailableActions,
    loadEntries,
    setPage,
    setPageSize
  } = useJournalEntries();
  const { 
    entries: agentEntries, 
    loading: agentLoading, 
    validateEntry, 
    validateBatchEntries,
    rejectEntry: rejectAgentEntry,
    updateEntry: updateAgentEntry,
    deleteEntry: deleteAgentEntry,
    loadEntries: loadAgentEntries
  } = useAgentEntries();

  const { reverseEntry, loading: reverseLoading } = useJournalReversal();
  // Fonction pour rafraîchir toutes les entrées (journal + agent)
  const handleRefresh = async () => {
    await Promise.all([loadEntries(), loadAgentEntries()]);
  };
  
  // Combiner toutes les entrées pour le filtrage avec protection renforcée
  const safeEntries = Array.isArray(entries) ? entries : [];
  const safeAgentEntries = Array.isArray(agentEntries) ? agentEntries : [];
  const allEntries = [...safeEntries, ...safeAgentEntries];
  
  // Vérification supplémentaire pour s'assurer qu'on a toujours un tableau
  if (!Array.isArray(allEntries)) {
    console.error('allEntries is not an array:', allEntries);
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-600">Erreur: Données invalides</div>
      </div>
    );
  }
  
  // Utiliser les nouveaux filtres améliorés
  const {
    filters,
    updateFilters,
    resetFilters,
    filteredEntries,
    isFiltersActive,
    filterStats,
    getJournalTypeOptions,
    getStatusOptions,
    getSourceOptions
  } = useEnhancedJournalFilters(allEntries);
  
  // Hook pour les informations d'organisation (export)
  const { organization } = useOrganization();

  // Hook pour l'export
  const {
    loading: isExporting,
    error: exportError,
    handleExport: exportJournal
  } = useJournalExport();

  const handleNewEntry = async (data: Partial<JournalEntry>) => {
    try {
      await addEntry(data);
      setShowNewEntryModal(false);
    } catch (error) {
      console.error('Failed to add journal entry:', error);
    }
  };

  const handleView = (entry: JournalEntry) => {
    setSelectedEntry(entry);
    setShowViewModal(true);
  };

  const handleViewAttachment = (entry: JournalEntry, attachmentId: string) => {
    const attachment = entry.attachments?.find(att => att.id === attachmentId);
    if (attachment?.url) {
      window.open(attachment.url, '_blank');
    } else if (attachment?.localUrl) {
      window.open(attachment.localUrl, '_blank');
    }
  };

  const handleUploadAttachment = async (entry: JournalEntry, file: File) => {
    try {
      toast.loading('Upload en cours...', { id: 'attachment-upload' });
      // Upload vers Cloudinary puis mise à jour de l'écriture avec l'URL
      const result = await CloudinaryService.uploadJournalAttachment(file);
      
      const newAttachment = {
        id: crypto.randomUUID(),
        name: file.name,
        url: result.url,
        mimeType: file.type,
        size: result.size,
        status: 'uploaded' as const
      };

      const updatedAttachments = [...(entry.attachments || []), newAttachment];

      // Aiguillage : les entrées proposées par l'agent (source='agent') ont
      // leur propre endpoint (/agent-entries/:id) et leur propre hook.
      // updateEntry() ne connaît que les écritures du journal classique —
      // appeler updateEntry sur une agent_entry lèverait "Entry not found"
      // alors que Cloudinary aurait déjà uploadé le fichier (waste).
      if (entry.source === 'agent') {
        await updateAgentEntry(entry.id, { attachments: updatedAttachments });
      } else {
        await updateEntry(entry.id, { attachments: updatedAttachments });
      }

      // Mettre à jour selectedEntry si c'est l'entrée en cours de visualisation
      if (selectedEntry?.id === entry.id) {
        setSelectedEntry(prev => prev ? { ...prev, attachments: updatedAttachments } : prev);
      }

      toast.success('Pièce jointe ajoutée avec succès', { id: 'attachment-upload' });
    } catch (error) {
      console.error('Failed to upload attachment:', error);
      toast.error('Erreur lors de l\'upload de la pièce jointe. Veuillez réessayer.', { id: 'attachment-upload' });
    }
  };

  const handleDownloadAttachment = async (entry: JournalEntry, attachmentId: string) => {
    try {
      const attachment = entry.attachments?.find(att => att.id === attachmentId);
      if (attachment?.url) {
        // Ouvrir l'URL Cloudinary dans un nouvel onglet pour téléchargement
        window.open(attachment.url, '_blank');
      } else if (attachment?.localUrl) {
        // Fallback vers l'URL locale (mode offline)
        const link = document.createElement('a');
        link.href = attachment.localUrl;
        link.download = attachment.name;
        link.click();
      }
    } catch (error) {
      console.error('Failed to download attachment:', error);
    }
  };

  const handleEdit = (entry: JournalEntry) => {
    // Les écritures peuvent être éditées si l'utilisateur a la permission de création
    // et que l'exercice est ouvert
    setSelectedEntry(entry);
    setShowEditModal(true);
  };

  const handleDelete = async (entry: JournalEntry) => {
    if (!canDeleteEntry) return;
    try {
      if (entry.source === 'agent') {
        await deleteAgentEntry(entry.id);
      } else {
        await deleteEntry(entry.id);
      }
      toast.success('Écriture supprimée');
    } catch (error) {
      console.error('Failed to delete entry:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleReverse = (entry: JournalEntry) => {
    setSelectedEntry(entry);
    setShowReverseModal(true);
  };

  const handleReverseConfirm = async (data: { reversalDate?: string; description?: string }) => {
    if (!selectedEntry) return;
    try {
      await reverseEntry(selectedEntry.id, data);
      setShowReverseModal(false);
      setSelectedEntry(null);
      toast.success('Écriture d\'extourne créée avec succès');
      await handleRefresh();
    } catch (error) {
      console.error('Failed to reverse entry:', error);
      toast.error('Erreur lors de l\'extourne');
    }
  };

  const handleUpdate = async (updatedEntry: JournalEntry) => {
    try {
      await updateEntry(updatedEntry.id, updatedEntry);
      setShowEditModal(false);
      setSelectedEntry(null);
    } catch (error) {
      console.error('Failed to update journal entry:', error);
    }
  };

  const handleExport = async (format: 'pdf' | 'excel' | 'csv') => {
    try {
      await exportJournal({
        entries: filteredEntries,
        format,
        filters,
        organization,
        title: 'JOURNAL DES ÉCRITURES COMPTABLES'
      });
    } catch (error) {
      console.error(`Erreur lors de l'export ${format}:`, error);
    }
  };

  const handleAgentEntryUpdate = async (updatedEntry: JournalEntry) => {
    try {
      await updateAgentEntry(updatedEntry.id, updatedEntry);
      setSelectedEntry(null);
    } catch (error) {
      console.error('Failed to update agent entry:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleValidateEntry = async (entry: JournalEntry) => {
    if (!canValidateEntry) return;
    try {
      await validateEntry(entry.id);
    } catch (error) {
      console.error('Failed to validate entry:', error);
    }
  };

  // === Workflow status handlers ===
  const handleSubmitEntry = async (entry: JournalEntry) => {
    if (!canCreateEntry) { toast.error('Permission insuffisante'); return; }
    try {
      await submitEntry(entry.id);
      toast.success(`Écriture ${entry.reference || ''} soumise pour approbation`);
    } catch (error) {
      console.error('Failed to submit entry:', error);
      toast.error('Erreur lors de la soumission');
    }
  };

  const handleApproveEntry = async (entry: JournalEntry) => {
    if (!canValidateEntry) { toast.error('Permission insuffisante'); return; }
    try {
      await approveEntry(entry.id);
      toast.success(`Écriture ${entry.reference || ''} approuvée`);
    } catch (error) {
      console.error('Failed to approve entry:', error);
      toast.error('Erreur lors de l\'approbation');
    }
  };

  const handleRejectEntry = async (entry: JournalEntry, reason: string) => {
    try {
      await rejectEntry(entry.id, reason);
      toast.success(`Écriture ${entry.reference || ''} rejetée`);
    } catch (error) {
      console.error('Failed to reject entry:', error);
      toast.error('Erreur lors du rejet');
    }
  };

  const handlePostEntry = async (entry: JournalEntry) => {
    if (!canValidateEntry) { toast.error('Permission insuffisante'); return; }
    try {
      await postEntry(entry.id);
      toast.success(`Écriture ${entry.reference || ''} comptabilisée`);
    } catch (error) {
      console.error('Failed to post entry:', error);
      toast.error('Erreur lors de la comptabilisation');
    }
  };

  const handleRejectAgentEntry = async (entry: JournalEntry, reason: string) => {
    try {
      await rejectAgentEntry(entry.id, reason);
      toast.success(`Écriture ADHA ${entry.reference || ''} rejetée`);
    } catch (error) {
      console.error('Failed to reject agent entry:', error);
      toast.error('Erreur lors du rejet de l\'écriture ADHA');
    }
  };

  // === Batch status handlers ===
  const handleBatchSubmit = async (ids: string[]) => {
    if (!canCreateEntry) { toast.error('Permission insuffisante'); return; }
    try {
      for (const id of ids) await submitEntry(id);
      toast.success(`${ids.length} écriture(s) soumise(s) pour approbation`);
    } catch (error) {
      console.error('Failed to batch submit:', error);
      toast.error('Erreur lors de la soumission en lot');
    }
  };

  const handleBatchApprove = async (ids: string[]) => {
    if (!canValidateEntry) { toast.error('Permission insuffisante'); return; }
    try {
      for (const id of ids) await approveEntry(id);
      toast.success(`${ids.length} écriture(s) approuvée(s)`);
    } catch (error) {
      console.error('Failed to batch approve:', error);
      toast.error('Erreur lors de l\'approbation en lot');
    }
  };

  const handleBatchPost = async (ids: string[]) => {
    if (!canValidateEntry) { toast.error('Permission insuffisante'); return; }
    try {
      for (const id of ids) await postEntry(id);
      toast.success(`${ids.length} écriture(s) comptabilisée(s)`);
    } catch (error) {
      console.error('Failed to batch post:', error);
      toast.error('Erreur lors de la comptabilisation en lot');
    }
  };

  const handleValidateBatchEntries = async (entries: JournalEntry[]) => {
    if (!canValidateEntry) return;
    try {
      const entryIds = entries.map(entry => entry.id);
      await validateBatchEntries(entryIds);
      toast.success(`${entryIds.length} écriture(s) validée(s)`);
    } catch (error) {
      console.error('Failed to validate batch entries:', error);
      toast.error('Erreur lors de la validation');
    }
  };

  // Pagination côté serveur - les entrées renvoyées par le hook sont déjà paginées
  const normalEntries = filteredEntries.filter(entry => entry.source !== 'agent');
  const adhaEntries = filteredEntries.filter(entry => entry.source === 'agent');
  
  // Utiliser les bonnes entrées selon le filtre source
  const entriesToDisplay = filters.source === 'agent' ? adhaEntries : 
                           filters.source === 'manual' ? normalEntries : 
                           filteredEntries;
  
  // La pagination est gérée par le backend via le hook
  const currentPage = pagination.page;
  const totalPages = pagination.totalPages;
  const totalItems = pagination.total;
  
  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const handlePageSizeChange = (pageSize: number) => {
    setPageSize(pageSize);
  };

  if (loading || agentLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-muted border-t-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête avec actions */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Journal des Écritures</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Gestion et consultation des écritures comptables</p>
        </div>
        {canCreateEntry && (
          <Button
            variant="success"
            icon={Plus}
            onClick={() => setShowNewEntryModal(true)}
            disabled={!currentFiscalYear || currentFiscalYear.status === 'closed'}
          >
            Nouvelle Écriture
          </Button>
        )}
      </div>

      {/* Filtres améliorés */}
      <EnhancedJournalFilters
        filters={filters}
        onFiltersChange={updateFilters}
        onReset={resetFilters}
        filterStats={filterStats}
        isFiltersActive={isFiltersActive}
        journalTypeOptions={getJournalTypeOptions}
        statusOptions={getStatusOptions}
        sourceOptions={getSourceOptions}
      />

      {/* Message d'erreur d'export */}
      {exportError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">
            Erreur d'export: {exportError}
          </p>
        </div>
      )}

      {/* Liste unifiée des écritures */}
      <UnifiedJournalList
        entries={entriesToDisplay}
        agentEntries={agentEntries}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onReverse={handleReverse}
        onValidate={handleValidateEntry}
        onValidateAll={handleValidateBatchEntries}
        onSubmitEntry={handleSubmitEntry}
        onApproveEntry={handleApproveEntry}
        onRejectEntry={handleRejectEntry}
        onPostEntry={handlePostEntry}
        onRejectAgentEntry={handleRejectAgentEntry}
        onBatchSubmit={handleBatchSubmit}
        onBatchApprove={handleBatchApprove}
        onBatchPost={handleBatchPost}
        getAvailableActions={getAvailableActions}
        onUploadAttachment={handleUploadAttachment}
        onDownloadAttachment={handleDownloadAttachment}
        onExport={handleExport}
        onRefresh={handleRefresh}
        loading={isExporting}
        page={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {/* Modales */}
      {showNewEntryModal && (
        <NewJournalEntryModal
          isOpen={showNewEntryModal}
          onClose={() => setShowNewEntryModal(false)}
          onSubmit={handleNewEntry}
        />
      )}

      {showViewModal && selectedEntry && (
        <Modal 
          isOpen={showViewModal}
          onClose={() => setShowViewModal(false)}
          title="Détails de l'écriture"
          size="xl"
          noPadding
        >
          <JournalEntryView
            entry={selectedEntry}
            onViewAttachment={(attachmentId) => handleViewAttachment(selectedEntry, attachmentId)}
            onDownloadAttachment={(attachmentId) => handleDownloadAttachment(selectedEntry, attachmentId)}
            onUploadAttachment={selectedEntry.status !== 'posted' ? (file) => handleUploadAttachment(selectedEntry, file) : undefined}
          />
        </Modal>
      )}

      {showEditModal && selectedEntry && (
        <JournalEntryEditModal
          entry={selectedEntry}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSave={handleUpdate}
        />
      )}

      {showReverseModal && selectedEntry && (
        <ReverseJournalEntryModal
          entry={selectedEntry}
          isOpen={showReverseModal}
          onClose={() => { setShowReverseModal(false); setSelectedEntry(null); }}
          onConfirm={handleReverseConfirm}
          loading={reverseLoading}
        />
      )}


    </div>
  );
}
